import * as React from "react";
import { Link, type LinkProps } from "react-router-dom";

import { cn } from "../../lib/utils";

/**
 * The shared "this value leads somewhere" affordance, for a value rendered in place — a cell in a table, a
 * figure in a report, a field in a detail sheet.
 *
 * IT DOES NOT RECOLOUR THE TEXT, and that is the whole point.
 *
 * The colour of a value often already carries meaning: green is income, red is an expense or a negative,
 * amber is over budget, muted is an empty state. Painting a value link-blue because it happens to be clickable
 * OVERWRITES that meaning with a fact about routing — and a treasurer reading a green figure turned blue has
 * to work out whether the colour is telling them something about the money. It isn't.
 *
 * Nor is it `hover:underline`, which most tables reached for instead: an affordance you can only discover by
 * hovering is one a user has to go looking for, cell by cell.
 *
 * So the affordance is a dotted underline that firms up on hover. The value keeps its own colour, says plainly
 * that it leads somewhere, and stays readable in a printed report where colour is gone entirely.
 */
export const valueLinkClass =
  "rounded-sm text-inherit underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:outline-2 focus-visible:outline-offset-2";

export type ValueLinkProps = LinkProps & {
  children: React.ReactNode;
};

/**
 * A value that links to what it stands for — a balance to its ledger, a reference to its audit, a name to its
 * record.
 *
 * Use it for the value ITSELF. Buttons, "view all" links and toolbar actions are ordinary navigation and
 * should look like it.
 *
 * When the target is not a URL (a dialog, a sheet, an in-page investigation), apply {@link valueLinkClass} to
 * the button instead. Prefer a real href wherever one exists: it stays keyboard-reachable, middle-clickable
 * and copyable, which is what someone auditing a figure actually does with it.
 */
export function ValueLink({ className, children, ...props }: ValueLinkProps) {
  return (
    <Link {...props} className={cn(valueLinkClass, className)}>
      {children}
    </Link>
  );
}
